import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { CompanyAddress, SendingDomain } from "@vd/api-client";
import { IconButton, Menu, Surface, Table, TBody, Td, THead, Th, Tr } from "@vd/ui";
import { ChevronRight, Globe, MoreHorizontal, Plus } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { meQuery, qk } from "../../../api/admin";
import { errorMessage } from "../../../api/client";
import {
  addressesQuery,
  companyHasEmailMarketing,
  domainsQuery,
  sending,
  sendingKeys,
  sendingPolicyQuery,
} from "../../../api/sending";
import type { Me } from "../../../api/types";
import { requireAdmin } from "../../../auth/useMe";
import { AddressDrawer } from "../../../components/sending/AddressDrawer";
import { DomainDrawer } from "../../../components/sending/DomainDrawer";
import { SettingsGroup, StatePill } from "../../../components/sending/parts";
import { SendingPolicyRow } from "../../../components/sending/SendingPolicy";
import { toast } from "../../../components/toast";
import { Button, Dialog, ErrorState, Loading, PageHeader } from "../../../components/ui";
import { level } from "../../../lib/crumbs";
import { useNavigate } from "../../../lib/navigate";
import { addressBulk, dnsPill, healthPill, reaches, TRANSPORT_LABEL } from "../../../lib/sending";

// Email sending (DESIGN.md "Console: Email sending", its "As built" block, screens h, h2, i to
// i5, m, n; spec 2.4 and 2.20.8; story EM29): the company's sending domains and addresses on the
// APIs that exist today. The Setup score (EM46), Used for, Mail and the Mailbox column (EM37)
// wait for their APIs. Drawers open from the search so a link (and a screenshot) lands on one.

const searchSchema = z.object({
  /** The domain drawer. */
  domain: z.string().optional().catch(undefined),
  /** An address's drawer. */
  address: z.string().optional().catch(undefined),
  /** Add domain or Add address. */
  add: z.enum(["domain", "address"]).optional().catch(undefined),
});

export const Route = createFileRoute("/admin/integrations/email-sending")({
  staticData: {
    crumb: { label: "Email sending", parents: [level("Integrations", "/admin/integrations")] },
  },
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: (ctx) => {
    requireAdmin(ctx);
    const me = ctx.context.queryClient.getQueryData<Me>(qk.me);
    if (me && !companyHasEmailMarketing(me)) throw redirect({ to: "/admin/integrations" });
  },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.prefetchQuery(domainsQuery),
      context.queryClient.prefetchQuery(addressesQuery),
      context.queryClient.prefetchQuery(sendingPolicyQuery),
    ]),
  component: EmailSendingPage,
});

const TILE = "inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-fill-2 text-ink-2";

function EmailSendingPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const domains = useQuery(domainsQuery);
  const addresses = useQuery(addressesQuery);
  const me = useQuery(meQuery);
  const [archiving, setArchiving] = useState<CompanyAddress | null>(null);
  const open = (s: z.infer<typeof searchSchema>) => void navigate({ search: s, replace: true });
  const close = () => open({});

  const domainList = domains.data?.items ?? [];
  const addressList = addresses.data?.items ?? [];
  const editing = search.address ? (addressList.find((a) => a.id === search.address) ?? null) : null;
  // Empty (h2): Add domain is the primary action and Add address waits for a domain or address.
  const empty = domains.isSuccess && addresses.isSuccess && !domainList.length && !addressList.length;

  if (me.data && !companyHasEmailMarketing(me.data)) return null;

  return (
    <>
      <PageHeader
        title="Email sending"
        actions={
          <>
            <Button variant={empty ? "primary" : "secondary"} onClick={() => open({ add: "domain" })}>
              <Globe aria-hidden className="size-4" />
              Add domain
            </Button>
            {empty ? null : (
              <Button variant="primary" onClick={() => open({ add: "address" })}>
                <Plus aria-hidden className="size-4" />
                Add address
              </Button>
            )}
          </>
        }
      />
      <div className="flex max-w-[1180px] flex-col gap-6">
        <section aria-label="Domains" className="flex flex-col gap-2">
          <h2 className="px-1 font-medium text-caption text-ink-3">Domains</h2>
          {domains.isPending ? (
            <Loading />
          ) : domains.isError ? (
            <ErrorState error={domains.error} onRetry={() => domains.refetch()} />
          ) : domainList.length ? (
            <DomainsTable domains={domainList} onOpen={(id) => open({ domain: id })} />
          ) : (
            <Surface radius="xl" className="flex min-h-16 items-center gap-3 px-4">
              <span className={TILE}>
                <Globe aria-hidden className="size-4" />
              </span>
              <span className="flex-1 text-ink text-ui">No sending domains</span>
              <Button size="sm" onClick={() => open({ add: "domain" })}>
                Add domain
              </Button>
            </Surface>
          )}
        </section>
        {addressList.length || addresses.isError ? (
          <section aria-label="Addresses" className="flex flex-col gap-2">
            <h2 className="px-1 font-medium text-caption text-ink-3">Addresses</h2>
            {addresses.isError ? (
              <ErrorState error={addresses.error} onRetry={() => addresses.refetch()} />
            ) : (
              <AddressesTable
                addresses={addressList}
                domains={domainList}
                onOpen={(id) => open({ address: id })}
                onArchive={setArchiving}
              />
            )}
          </section>
        ) : null}
        <SettingsGroup title="Policy">
          <SendingPolicyRow />
        </SettingsGroup>
      </div>

      <DomainDrawer
        id={search.domain ?? null}
        adding={search.add === "domain"}
        onClose={close}
        onAdded={(id) => open({ domain: id })}
      />
      <AddressDrawer
        open={search.add === "address" || editing !== null}
        address={editing}
        domains={domainList}
        onClose={close}
      />
      <ArchiveDialog address={archiving} onClose={() => setArchiving(null)} />
    </>
  );
}

function DomainsTable({ domains, onOpen }: { domains: SendingDomain[]; onOpen: (id: string) => void }) {
  return (
    <Surface radius="xl" className="overflow-hidden">
      <Table>
        <THead>
          <tr>
            <Th>Domain</Th>
            <Th>Bulk sending</Th>
            <Th>Health</Th>
            <Th className="w-12">
              <span className="sr-only">Open</span>
            </Th>
          </tr>
        </THead>
        <TBody>
          {domains.map((d) => (
            <Tr key={d.id} interactive className="h-[52px] cursor-pointer" onClick={() => onOpen(d.id)}>
              <Td>
                <button
                  type="button"
                  className="flex items-center gap-2 font-medium text-ink focus-visible:underline focus-visible:outline-none"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(d.id);
                  }}
                >
                  <Globe aria-hidden className="size-3.5 text-ink-3" />
                  {d.domain}
                </button>
              </Td>
              <Td>
                <StatePill pill={dnsPill(d)} />
              </Td>
              <Td>
                <StatePill pill={healthPill(d)} />
              </Td>
              <Td className="text-right">
                <ChevronRight aria-hidden className="ml-auto size-4 text-ink-3" />
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </Surface>
  );
}

function AddressesTable({
  addresses,
  domains,
  onOpen,
  onArchive,
}: {
  addresses: CompanyAddress[];
  domains: SendingDomain[];
  onOpen: (id: string) => void;
  onArchive: (a: CompanyAddress) => void;
}) {
  return (
    <Surface radius="xl" className="overflow-hidden">
      <Table>
        <THead>
          <tr>
            <Th>Address</Th>
            <Th>Mailbox</Th>
            <Th>Bulk sending</Th>
            <Th>Reaches</Th>
            <Th>Sent, 30 days</Th>
            <Th className="w-12">
              <span className="sr-only">More</span>
            </Th>
          </tr>
        </THead>
        <TBody>
          {addresses.map((a) => {
            const bulk = addressBulk(a, domains);
            return (
              <Tr key={a.id} interactive className="h-[52px] cursor-pointer" onClick={() => onOpen(a.id)}>
                <Td>
                  <button
                    type="button"
                    className="flex min-w-0 flex-col text-left focus-visible:underline focus-visible:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(a.id);
                    }}
                  >
                    <span className="truncate font-medium text-ink">{a.address}</span>
                    {a.display_name ? (
                      <span className="truncate text-caption text-ink-3">{a.display_name}</span>
                    ) : null}
                  </button>
                </Td>
                <Td>
                  {a.transport === "kite" ? (
                    <span className="text-ink-4">None</span>
                  ) : (
                    <span className="text-ink">{TRANSPORT_LABEL[a.transport]}</span>
                  )}
                </Td>
                <Td>
                  {bulk === "off" ? (
                    <span className="text-ink-4">Off</span>
                  ) : bulk === "not_verified" ? (
                    <span className="text-ink-2">Domain not verified</span>
                  ) : (
                    <StatePill pill={bulk} />
                  )}
                </Td>
                <Td className="whitespace-nowrap text-ink-2">{reaches(a.transport)}</Td>
                <Td className="tabular-nums">{a.sent_30d.toLocaleString("en-US")}</Td>
                <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Menu
                    trigger={
                      <IconButton
                        size="sm"
                        label={`More for ${a.address}`}
                        icon={<MoreHorizontal aria-hidden />}
                      />
                    }
                    items={[
                      { label: "Edit", onSelect: () => onOpen(a.id) },
                      { label: "Archive", danger: true, onSelect: () => onArchive(a) },
                    ]}
                  />
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </Surface>
  );
}

function ArchiveDialog({ address, onClose }: { address: CompanyAddress | null; onClose: () => void }) {
  const qc = useQueryClient();
  const archive = useMutation({
    mutationFn: (id: string) => sending.archiveAddress(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sendingKeys.all });
      toast(`Archived ${address?.address ?? "the address"}.`);
      onClose();
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  return (
    <Dialog
      open={address !== null}
      onClose={onClose}
      tone="danger"
      size="sm"
      title={`Archive ${address?.address ?? ""}?`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            busy={archive.isPending}
            onClick={() => {
              if (address) archive.mutate(address.id);
            }}
          >
            Archive
          </Button>
        </>
      }
    />
  );
}
